/**
 * Listener lists for one mount.
 *
 * Listeners run in subscription order. A throwing listener is contained: the bus logs one line
 * naming the bus label (an `appId@version/surface` string, never a payload) and the rest still
 * run. After {@link OutputBus.close} every method is a no-op and `on`/`onRuntime` return a no-op
 * unsubscribe. Unsubscribe functions are idempotent.
 */
export class OutputBus {
    #label;
    #outputs = new Map();
    #runtime = [];
    #closed = false;
    /** `label` names the mount in a log line: `appId@version/surface`. */
    constructor(label) {
        this.#label = label;
    }
    /** True after {@link close}. */
    get closed() {
        return this.#closed;
    }
    /** Subscribe to one output name. Returns an idempotent unsubscribe. */
    on(output, listener) {
        if (this.#closed)
            return noop;
        const list = this.#outputs.get(output) ?? [];
        list.push(listener);
        this.#outputs.set(output, list);
        return () => {
            const current = this.#outputs.get(output);
            const at = current?.indexOf(listener) ?? -1;
            if (at >= 0)
                current.splice(at, 1);
        };
    }
    /** Subscribe to this mount's runtime events. Returns an idempotent unsubscribe. */
    onRuntime(listener) {
        if (this.#closed)
            return noop;
        this.#runtime.push(listener);
        return () => {
            this.#runtime = this.#runtime.filter((l) => l !== listener);
        };
    }
    /** Call every listener of `output` with `payload`, in subscription order. */
    deliver(output, payload) {
        if (this.#closed)
            return;
        // A snapshot, so a listener that unsubscribes mid-delivery skips no other listener.
        for (const listener of (this.#outputs.get(output) ?? []).slice())
            this.#call(() => listener(payload));
    }
    /** Call every runtime listener with `event`, in subscription order. */
    deliverRuntime(event) {
        if (this.#closed)
            return;
        for (const listener of this.#runtime.slice())
            this.#call(() => listener(event));
    }
    /** Drop every listener and refuse further work. Idempotent. */
    close() {
        this.#closed = true;
        this.#outputs.clear();
        this.#runtime = [];
    }
    #call(run) {
        try {
            run();
        }
        catch {
            console.error(`[sdk-surfaces] a listener threw on ${this.#label}; the other listeners still ran`);
        }
    }
}
function noop() { }
//# sourceMappingURL=output-bus.js.map